import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Chip,
    Divider,
    IconButton,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Collapse,
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    AdminPanelSettings as RoleIcon,
    CheckCircle as CheckCircleIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useForm, Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { request } from '../../../../../utils/request';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { ProfileFormData, UserProfile } from '../../../types';

const ProfilePage: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userData, setUserData] = useState<UserProfile | null>(null);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);
    const location = useLocation();
    const userId = location?.state?.userId;

    const {
        control,
        handleSubmit,
        reset,
    } = useForm<ProfileFormData>({
        defaultValues: {
            name: '',
            userName: '',
            email: '',
            phoneNumber: '',
            address: {
                line1: '',
                city: '',
                state: '',
                country: '',
                pin_code: '',
            },
        },
    });

    // Check if user is admin
    const isAdmin = userData?.role?.name?.toLowerCase() === 'admin';

    // Fetch user profile
    const fetchUserProfile = useCallback(async () => {
        try {
            setLoading(true);

            const response = await request({
                url: `auth/get-single-user/${userId}`,
                method: 'get',
            });

            if (response.status === 'success' && response.data) {
                const user = response.data;
                setUserData(user);

                reset({
                    name: user.name || '',
                    userName: user.userName || '',
                    email: user.email || '',
                    phoneNumber: user.phoneNumber || '',
                    address: {
                        line1: user.address?.line1 || '',
                        city: user.address?.city || '',
                        state: user.address?.state || '',
                        country: user.address?.country || '',
                        pin_code: user.address?.pin_code || '',
                    },
                });
            }
        } catch (error: any) {
            console.error('Error fetching profile:', error);
            toast.error(error?.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    }, [userId, reset]);

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    // Handle profile update
    const onUpdateProfile = async (data: ProfileFormData) => {
        try {
            setSaving(true);

            const response = await request({
                url: `auth/update-user/${userId}`,
                method: 'put',
                data: {
                    name: data.name,
                    userName: data.userName,
                    email: data.email,
                    phoneNumber: data.phoneNumber,
                    address: data.address,
                },
            });

            if (response.status === 'success') {
                toast.success('Profile updated successfully');
                setIsEditing(false);
                fetchUserProfile();
            }
        } catch (error: any) {
            console.error('Error updating profile:', error);
            toast.error(error?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    // Handle password change
    const handlePasswordChange = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        try {
            setSaving(true);

            const response = await request({
                url: `auth/change-password/${userId}`,
                method: 'put',
                data: {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword,
                },
            });

            if (response.status === 'success') {
                toast.success('Password changed successfully');
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
                setShowPasswordForm(false);
            }
        } catch (error: any) {
            console.error('Error changing password:', error);
            toast.error(error || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    // Get status chip color
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'suspended':
                return 'error';
            case 'deactivated':
                return 'default';
            default:
                return 'default';
        }
    };

    // Group permissions by category
    const groupPermissionsByCategory = (permissions: string[]) => {
        const categories: { [key: string]: string[] } = {
            'Dashboard': [],
            'User Management': [],
            'Role Management': [],
            'Employee Management': [],
            'Inventory Management': [],
            'Enquiry Management': [],
            'Product Return': [],
            'Party Management': [],
            'Vendor Management': [],
            'Reports': [],
            'Settings': [],
            'Ledger': [],
            'Price List': [],
            'Attendance': [],
            'Gst Configuration': [],
            'Profile': [],
            'Price Code': [],
            'Brands': [],
            'Categories': [],
            'Cars': [],
            'Sales Invoice': [],
            'Quotation': [],
            'Payment In': [],
            'Sales Return': [],
            'Credit Note': [],
            'Purchase Invoice': [],
            'Payment Out': [],
            'Purchase Return': [],
            'Debit Note': []
        };

        permissions?.forEach(permission => {
            if (permission.startsWith('dashboard:')) categories['Dashboard'].push(permission);
            else if (permission.startsWith('user:')) categories['User Management'].push(permission);
            else if (permission.startsWith('role:')) categories['Role Management'].push(permission);
            else if (permission.startsWith('employee:')) categories['Employee Management'].push(permission);
            else if (permission.startsWith('inventory:')) categories['Inventory Management'].push(permission);
            else if (permission.startsWith('enquiry:')) categories['Enquiry Management'].push(permission);
            else if (permission.startsWith('product-return:')) categories['Product Return'].push(permission);
            else if (permission.startsWith('party:')) categories['Party Management'].push(permission);
            else if (permission.startsWith('vendor:')) categories['Vendor Management'].push(permission);
            else if (permission.startsWith('report:')) categories['Reports'].push(permission);
            else if (permission.startsWith('settings:')) categories['Settings'].push(permission);
            else if (permission.startsWith('ledger:')) categories['Ledger'].push(permission);
            else if (permission.startsWith('price-list:')) categories['Price List'].push(permission);
            else if (permission.startsWith('attendance:')) categories['Attendance'].push(permission);
            else if (permission.startsWith('gst-configuration:')) categories['Gst Configuration'].push(permission);
            else if (permission.startsWith('profile:')) categories['Profile'].push(permission);
            else if (permission.startsWith('price-code:')) categories['Price Code'].push(permission);
            else if (permission.startsWith('brand:')) categories['Brands'].push(permission);
            else if (permission.startsWith('category:')) categories['Categories'].push(permission);
            else if (permission.startsWith('car:')) categories['Cars'].push(permission);
            else if (permission.startsWith('sales-invoice:')) categories['Sales Invoice'].push(permission);
            else if (permission.startsWith('quotation:')) categories['Quotation'].push(permission);
            else if (permission.startsWith('payment-in:')) categories['Payment In'].push(permission);
            else if (permission.startsWith('sales-return:')) categories['Sales Return'].push(permission);
            else if (permission.startsWith('credit-note:')) categories['Credit Note'].push(permission);
            else if (permission.startsWith('purchase-invoice:')) categories['Purchase Invoice'].push(permission);
            else if (permission.startsWith('payment-out:')) categories['Payment Out'].push(permission);
            else if (permission.startsWith('purchase-return:')) categories['Purchase Return'].push(permission);
            else if (permission.startsWith('debit-note:')) categories['Debit Note'].push(permission);
        });

        // Filter out empty categories
        return Object.entries(categories).filter(([_, perms]) => perms.length > 0);
    };

    const groupedPermissions = userData?.role?.permissions ? groupPermissionsByCategory(userData.role.permissions) : [];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading profile...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>

            <Box sx={{}}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                        My Profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View and manage your account information
                    </Typography>
                </Box>

                {/* Profile Information Card */}
                <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 3, overflow: 'hidden' }}>
                    {/* Header with Edit Button */}
                    <Box sx={{
                        px: 3,
                        py: 2,
                        bgcolor: 'background.default',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                display: 'flex',
                                border: '1px solid #9e9e9e',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#9e9e9e'
                            }}>
                                <AccountCircleIcon />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Profile Information
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Your personal and contact details
                                </Typography>
                            </Box>
                        </Box>

                        {(!isEditing) && (
                            <Tooltip title="Edit Profile">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => setIsEditing(true)}
                                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                                >
                                    Edit
                                </Button>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3 }}>
                        {isEditing ? (
                            // Edit Mode Form
                            <form onSubmit={handleSubmit(onUpdateProfile)}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    {/* Basic Information */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: 'text.secondary' }}>
                                            Basic Information
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="name"
                                                    control={control}
                                                    rules={{ required: 'Name is required' }}
                                                    render={({ field, fieldState }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Full Name"
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="userName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Username"
                                                            size="small"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <BadgeIcon fontSize="small" color="action" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="email"
                                                    control={control}
                                                    rules={{
                                                        required: 'Email is required',
                                                        pattern: {
                                                            value: /^\S+@\S+$/i,
                                                            message: 'Invalid email'
                                                        }
                                                    }}
                                                    render={({ field, fieldState }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Email Address"
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                            size="small"
                                                            disabled={isAdmin ? false : true}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <EmailIcon fontSize="small" color="action" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="phoneNumber"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Phone Number"
                                                            size="small"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <PhoneIcon fontSize="small" color="action" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Address Information */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: 'text.secondary' }}>
                                            Address Information
                                        </Typography>

                                        <Controller
                                            name="address.line1"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Street Address"
                                                    size="small"
                                                    sx={{ mb: 2 }}
                                                />
                                            )}
                                        />

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="address.city"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="City"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="address.state"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="State"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="address.country"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Country"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="address.pin_code"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Postal Code"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Form Actions */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                reset({
                                                    name: userData?.name || '',
                                                    userName: userData?.userName || '',
                                                    email: userData?.email || '',
                                                    phoneNumber: userData?.phoneNumber || '',
                                                    address: {
                                                        line1: userData?.address?.line1 || '',
                                                        city: userData?.address?.city || '',
                                                        state: userData?.address?.state || '',
                                                        country: userData?.address?.country || '',
                                                        pin_code: userData?.address?.pin_code || '',
                                                    },
                                                });
                                                setIsEditing(false);
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="small"
                                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                            disabled={saving}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                        ) : (
                            // View Mode
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {/* Basic Information */}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                        Basic Information
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Full Name</Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                                                {userData?.name || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Username</Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                                                {userData?.userName || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Email Address</Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                                                {userData?.email || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                                                {userData?.phoneNumber || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider />

                                {/* Role & Status */}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                        Role & Status
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Role</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <RoleIcon fontSize="small" color="primary" />
                                                <Typography variant="body2" fontWeight={500}>
                                                    {userData?.role?.name || '-'}
                                                </Typography>
                                                {userData?.role?.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({userData.role.description})
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ flex: '1 1 200px' }}>
                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip
                                                    label={userData?.status || 'Active'}
                                                    size="small"
                                                    color={getStatusColor(userData?.status || '') as any}
                                                    icon={<CheckCircleIcon />}
                                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Permissions Section - Only show if user has permissions */}
                                {userData?.role?.permissions && userData.role.permissions.length > 0 && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    cursor: 'pointer',
                                                    mb: 1.5
                                                }}
                                                onClick={() => setShowPermissions(!showPermissions)}
                                            >
                                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <VpnKeyIcon fontSize="small" color="primary" />
                                                    Permissions ({userData.role.permissions.length})
                                                </Typography>
                                                <IconButton size="small">
                                                    {showPermissions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>

                                            <Collapse in={showPermissions}>
                                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {groupedPermissions.map(([category, permissions]) => (
                                                        <Box key={category}>
                                                            <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'primary.main' }}>
                                                                {category}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                {permissions.map((perm) => (
                                                                    <Chip
                                                                        key={perm}
                                                                        label={perm.split(':')[1] || perm}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', height: 24 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    </>
                                )}

                                {/* Address Information */}
                                {userData?.address && (userData.address.line1 || userData.address.city) && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                                Address Information
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <LocationIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                                                <Box>
                                                    {userData.address.line1 && (
                                                        <Typography variant="body2">{userData.address.line1}</Typography>
                                                    )}
                                                    {(userData.address.city || userData.address.state) && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {[userData.address.city, userData.address.state].filter(Boolean).join(', ')}
                                                        </Typography>
                                                    )}
                                                    {(userData.address.country || userData.address.pin_code) && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {[userData.address.country, userData.address.pin_code].filter(Boolean).join(' - ')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Security Section - Only for Admin */}
                {(
                    <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                        <Box sx={{
                            px: 3,
                            py: 2,
                            bgcolor: 'background.default',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    display: 'flex',
                                    border: '1px solid #9e9e9e',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#9e9e9e'
                                }}>
                                    <LockIcon />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Security
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Manage your password and security settings
                                    </Typography>
                                </Box>
                            </Box>

                            {!showPasswordForm && (
                                <Tooltip title="Change Password">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => setShowPasswordForm(true)}
                                        sx={{ borderRadius: 1.5, textTransform: 'none' }}
                                    >
                                        Change Password
                                    </Button>
                                </Tooltip>
                            )}
                        </Box>

                        <Box sx={{ p: 3 }}>
                            {showPasswordForm ? (
                                <Box display='flex' flexDirection='column'>
                                    <Box display='flex' gap={2}>
                                        <TextField
                                            fullWidth
                                            type={showPassword ? 'text' : 'password'}
                                            label="Current Password"
                                            value={passwordData.currentPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="New Password"
                                            value={passwordData.newPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            helperText="Password must be at least 6 characters"
                                        />
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Confirm New Password"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                            size="small"
                                            sx={{ mb: 2 }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                setShowPasswordForm(false);
                                                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handlePasswordChange}
                                            disabled={saving}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {saving ? <CircularProgress size={16} /> : 'Update Password'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                    <Typography variant="body2">
                                        For security, we recommend changing your password every 90 days. Use a strong password with a mix of letters, numbers, and symbols.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        </>
    );
};

export default ProfilePage;